import { memo, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { graphClient } from '../../services/graph-client'

/**
 * Página do CP (Couple Challenge) — layout ref "Sweet CP Star".
 * Aberta pelo banner da home via openWebPage: /cp?fullscreen=1&token=<jwt>
 * (o graph-client pega o token da URL sozinho; o app fecha no postMessage 'close').
 *
 * Arte gerada no flux (fundo pétalas + hero de asas + moldura roxa do casal);
 * os avatares são posicionados por % medidos nos PNGs (buracos/centros).
 */

const ASSETS = {
  bg: 'https://storage.didderlive.com/feed/55000060/e6cdfac2-5615-47e4-a2f7-d2ac3a9d22fc.jpg',
  hero: 'https://storage.didderlive.com/feed/55000060/6b438790-fc7d-468c-b7d0-cb731a17117d.png',
  frame: 'https://storage.didderlive.com/feed/55000060/06ca9265-d440-4e56-8954-6bd9238f5efd.png',
}

interface CpChallenge {
  id: number
  name: string
  description: string | null
  imgBackground: string | null
  dateInitial: string | null
  dateFinal: string | null
}

interface CpRankingItem {
  id: number
  position: number
  totalValue: number
  userAUsername: string | null
  userAImgUser: string | null
  userBUsername: string | null
  userBImgUser: string | null
}

interface CpReward {
  id: number
  position: number
  typeBonus: 'coin' | 'point' | 'bean' | 'item'
  valueBonus: number
}

type ResponseJSON<T> = { status: string; error: string | null; data: T }

const REWARD_LABEL: Record<CpReward['typeBonus'], string> = {
  coin: 'Moedas',
  point: 'Pontos',
  bean: 'Beans',
  item: 'Item',
}

const REWARD_ICON: Record<CpReward['typeBonus'], string> = {
  coin: '🪙',
  point: '💎',
  bean: '🫘',
  item: '🎁',
}

const AVATAR_FALLBACK =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" rx="40" fill="%23731430"/><text x="40" y="52" font-size="34" text-anchor="middle">💘</text></svg>'

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0')

const formatValue = (v: number) =>
  v >= 1000000
    ? `${(v / 1000000).toFixed(2)} M`
    : v >= 1000
      ? `${(v / 1000).toFixed(1)} K`
      : String(v)

const useCountdown = (dateFinal?: string | null) => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  return useMemo(() => {
    if (!dateFinal) return null
    const diff = new Date(dateFinal).getTime() - now
    if (Number.isNaN(diff)) return null
    const total = Math.max(0, Math.floor(diff / 1000))
    return {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
      ended: total <= 0,
    }
  }, [dateFinal, now])
}

// wrapper com w/h explícitos: aspect-ratio em <img> perde pra proporção
// intrínseca da foto (retrato virava "pílula" vazando do aro)
const Avatar = ({ src, style }: { src: string | null; style: React.CSSProperties }) => (
  <div className="absolute overflow-hidden rounded-full" style={style}>
    <img
      src={src || AVATAR_FALLBACK}
      onError={(e) => ((e.target as HTMLImageElement).src = AVATAR_FALLBACK)}
      className="h-full w-full object-cover"
    />
  </div>
)

/**
 * Hero: asas vermelhas com 2 círculos (coroas). Avatares por % medidos no PNG:
 * centros L(31.4, 47.5) R(67.0, 47.5), diâmetro ≈ 29% da largura.
 */
const HeroWings = ({ top1 }: { top1?: CpRankingItem }) => {
  // os centros da arte são opacos (beige) -> avatar vai NA FRENTE, menor que o aro
  const DIA = 30
  // aspect-ratio em DIV é respeitado (em <img> a proporção intrínseca vence);
  // posiciona pelo CENTRO com translate — imune à base do height%
  const circle = (cx: number, cy: number): React.CSSProperties => ({
    width: `${DIA}%`,
    aspectRatio: '1 / 1',
    left: `${cx}%`,
    top: `${cy}%`,
    transform: 'translate(-50%, -50%)',
    zIndex: 20,
  })

  return (
    <div className="relative w-full" style={{ aspectRatio: '1024 / 640' }}>
      <img
        src={ASSETS.hero}
        className="absolute inset-0 z-10 h-full w-full object-contain select-none pointer-events-none"
      />
      {top1?.userAImgUser != null && (
        <Avatar src={top1?.userAImgUser} style={circle(31.0, 44.7)} />
      )}
      {top1?.userBImgUser != null && (
        <Avatar src={top1?.userBImgUser} style={circle(68.9, 44.5)} />
      )}
      {/* TOP 1 escrito dentro do coração da arte (centro medido: 50%, 78%) */}
      <div
        className="absolute z-30 -translate-x-1/2 -translate-y-1/2 text-center font-extrabold text-white"
        style={{
          left: '50%',
          top: '82%',
          fontSize: 12,
          textShadow: '0 1px 3px rgba(120,0,10,0.9)',
        }}
      >
        TOP 1
      </div>
    </div>
  )
}

/**
 * Card de casal com a moldura roxa/dourada. Centros medidos no PNG:
 * L(29.9, 49.8) R(69.9, 49.8), diâmetro ≈ 30% da largura (1024x576).
 */
const CoupleFrameCard = ({
  item,
  big,
}: {
  item: CpRankingItem
  big?: boolean
}) => {
  // ornamento 1024x640; furos transparentes: centros L(28.9,52) R(70.7,52).
  // avatar um pouco MAIOR que o furo (fica atrás, o aro cobre a sobra)
  const DIA = 33
  const CX_L = 28.9
  const CX_R = 70.7
  const circle = (cx: number): React.CSSProperties => ({
    width: `${DIA}%`,
    aspectRatio: '1 / 1',
    left: `${cx}%`,
    top: '52%',
    transform: 'translate(-50%, -50%)',
  })
  const nameStyle = (cx: number): React.CSSProperties => ({
    left: `${cx}%`,
    transform: 'translateX(-50%)',
    maxWidth: '42%',
  })

  return (
    <div className="rounded-3xl border border-rose-300/15 bg-black/30 px-2 pb-2 pt-4 backdrop-blur-[2px]">
      <div className="relative -mt-6 mb-1 flex justify-center">
        <span
          className={classNames(
            'z-30 rounded-full bg-gradient-to-b from-amber-200 via-amber-400 to-yellow-600 font-extrabold tracking-wider text-amber-950 shadow-lg',
            big ? 'px-5 py-0.5 text-xs' : 'px-3 py-0 text-[10px]',
          )}
        >
          TOP {item.position}
        </span>
      </div>
      <div className="relative w-full" style={{ aspectRatio: '1024 / 640' }}>
        <Avatar src={item.userAImgUser} style={circle(CX_L)} />
        <Avatar src={item.userBImgUser} style={circle(CX_R)} />
        <img
          src={ASSETS.frame}
          className="absolute inset-0 z-10 h-full w-full object-contain select-none pointer-events-none"
        />
      </div>
      <div className={classNames('relative w-full', big ? 'h-5 text-sm' : 'h-4 text-[11px]')}>
        <span className="absolute truncate font-bold" style={nameStyle(CX_L)}>
          {item.userAUsername ?? '—'}
        </span>
        <span className="absolute truncate font-bold" style={nameStyle(CX_R)}>
          {item.userBUsername ?? '—'}
        </span>
      </div>
      <div
        className={classNames(
          'mt-1 flex items-center justify-center gap-1 font-extrabold',
          big ? 'text-sm' : 'text-xs',
        )}
      >
        <span>💗</span>
        <span>{formatValue(item.totalValue)}</span>
      </div>
    </div>
  )
}

export const CpChallengePage = memo(() => {
  const [challenge, setChallenge] = useState<CpChallenge | null>(null)
  const [ranking, setRanking] = useState<CpRankingItem[]>([])
  const [rewards, setRewards] = useState<CpReward[]>([])
  const [tab, setTab] = useState<'ranking' | 'rewards'>('ranking')
  const [isLoading, setIsLoading] = useState(true)
  const [showHelp, setShowHelp] = useState(false)

  const countdown = useCountdown(challenge?.dateFinal)

  useEffect(() => {
    const load = async () => {
      try {
        const challengeRes = await graphClient.request<{
          getCpChallenges: ResponseJSON<CpChallenge[]>
        }>('query { getCpChallenges(onlyActive: true) { status error data } }')

        const active = challengeRes?.getCpChallenges?.data?.[0] ?? null
        setChallenge(active)

        const rankingRes = await graphClient.request<{
          getCpCoupleChallengeRanking: ResponseJSON<CpRankingItem[]>
        }>(
          `query { getCpCoupleChallengeRanking(${active ? `giftCpChallengeId: ${active.id}, ` : ''}limit: 50) { status error data } }`,
        )

        setRanking(rankingRes?.getCpCoupleChallengeRanking?.data ?? [])

        if (active) {
          const rewardsRes = await graphClient.request<{
            getCpChallengeRewards: ResponseJSON<CpReward[]>
          }>(
            `query { getCpChallengeRewards(giftCpChallengeId: ${active.id}) { status error data } }`,
          )
          setRewards(
            (rewardsRes?.getCpChallengeRewards?.data ?? []).sort(
              (a, b) => a.position - b.position,
            ),
          )
        }
      } catch {
        // silencia — a página mostra o estado vazio
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [])

  const top1 = ranking[0]
  const rest = ranking.slice(1)

  const goBack = () => {
    const rn = (window as unknown as {
      ReactNativeWebView?: { postMessage(msg: string): void }
    }).ReactNativeWebView
    if (rn) {
      rn.postMessage('close')
      return
    }
    window.history.back()
  }

  return (
    <div
      className="w-screen h-screen overflow-y-auto overflow-x-hidden font-[Nunito] text-white bg-[#2b0612] bg-cover bg-center bg-fixed"
      style={{ backgroundImage: `url(${ASSETS.bg})` }}
    >
      <div className="relative z-10 mx-auto flex max-w-md flex-col items-center px-4 pb-10 pt-4">
        {/* topo: voltar / título / ajuda */}
        <div className="mb-1 flex w-full items-center justify-between">
          <button
            onClick={goBack}
            aria-label="Voltar"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-200/40 bg-black/25 text-xl text-rose-100 backdrop-blur-sm active:opacity-70"
          >
            ↩
          </button>

          {/* mesmo box nas 2 camadas (sem padding no container — inset-0
              ignora padding e as linhas quebravam em pontos diferentes) */}
          <div className="relative mx-1 text-center">
            <h1
              aria-hidden
              className="text-[26px] font-extrabold italic leading-8"
              style={{ WebkitTextStroke: '4px rgba(255,245,243,0.95)' }}
            >
              {challenge?.name ?? 'Desafio CP'}
            </h1>
            <h1 className="absolute inset-0 bg-gradient-to-b from-rose-400 via-red-500 to-red-700 bg-clip-text text-[26px] font-extrabold italic leading-8 text-transparent">
              {challenge?.name ?? 'Desafio CP'}
            </h1>
          </div>

          <button
            onClick={() => setShowHelp(true)}
            aria-label="Ajuda"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-200/40 bg-black/25 text-lg font-bold text-rose-100 backdrop-blur-sm active:opacity-70"
          >
            ?
          </button>
        </div>

        {/* hero de asas com o casal TOP 1 */}
        <div className="mt-2 w-[92%]">
          <HeroWings top1={top1} />
        </div>

        {/* countdown */}
        <div className="mt-5 w-full rounded-full bg-gradient-to-r from-fuchsia-600/85 via-pink-500/85 to-rose-300/85 px-5 py-2.5 text-center text-[13px] font-bold tracking-wide shadow-lg">
          {countdown
            ? countdown.ended
              ? 'Desafio encerrado'
              : `Termina em | ${pad(countdown.days)} dias | ${pad(countdown.hours)} : ${pad(countdown.minutes)} : ${pad(countdown.seconds)}`
            : ' '}
        </div>

        {/* tabs */}
        <div className="mt-4 flex w-full overflow-hidden rounded-full bg-white/10 backdrop-blur-sm">
          {(['ranking', 'rewards'] as const).map((key) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={classNames(
                'flex-1 py-2.5 text-sm font-bold transition-colors',
                tab === key
                  ? 'rounded-full bg-gradient-to-b from-red-600 to-red-800 text-white shadow'
                  : 'text-rose-100/70',
              )}
            >
              {key === 'ranking' ? 'Ranking' : 'Prêmios'}
            </button>
          ))}
        </div>

        {tab === 'ranking' ? (
          <>
            {/* TOP 1 grande */}
            {top1 && (
              <div className="mt-6 w-full">
                <CoupleFrameCard item={top1} big />
              </div>
            )}

            {/* demais em grade 2 colunas */}
            {Boolean(rest.length) && (
              <div className="mt-4 grid w-full grid-cols-2 gap-3">
                {rest.map((item) => (
                  <CoupleFrameCard key={item.id} item={item} />
                ))}
              </div>
            )}

            {!ranking.length && !isLoading && (
              <p className="py-8 text-center text-sm text-rose-100/75">
                Seja o primeiro casal do ranking! 💘
                <br />
                <span className="text-xs text-rose-100/60">
                  Envie gifts de CP para entrar com seu par
                </span>
              </p>
            )}
          </>
        ) : (
          <div className="mt-5 w-full space-y-3">
            {rewards.map((reward) => (
              <div
                key={reward.id}
                className="flex items-center gap-3 rounded-2xl border border-rose-300/15 bg-black/25 px-4 py-3 backdrop-blur-[2px]"
              >
                <span
                  className={classNames(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-b text-xs font-extrabold shadow',
                    [
                      'from-amber-300 to-yellow-500 text-amber-950',
                      'from-slate-200 to-slate-400 text-slate-800',
                      'from-orange-300 to-amber-600 text-orange-950',
                    ][reward.position - 1] ?? 'from-rose-800 to-rose-900 text-rose-100',
                  )}
                >
                  {reward.position}
                </span>
                <span className="text-2xl">{REWARD_ICON[reward.typeBonus]}</span>
                <span className="flex-1 text-sm font-bold">
                  {reward.valueBonus.toLocaleString('pt-BR')} {REWARD_LABEL[reward.typeBonus]}
                </span>
              </div>
            ))}
            {!rewards.length && !isLoading && (
              <p className="py-6 text-center text-sm text-rose-100/70">Prêmios em breve ✨</p>
            )}
          </div>
        )}
      </div>

      {/* ajuda */}
      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-8"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl border border-rose-300/25 bg-[#3a0817] p-5 text-sm leading-relaxed text-rose-50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-2 text-base font-extrabold">Como funciona o CP 💘</p>
            <p>
              Envie <b>gifts de CP</b> para alguém especial: cada envio soma pontos
              pro casal de vocês. Os casais com mais pontos aparecem no ranking do
              desafio e ganham os prêmios ao final. O nível do casal (Crush até
              Casamento) cresce com o total acumulado.
            </p>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-4 w-full rounded-full bg-gradient-to-b from-red-600 to-red-800 py-2 font-bold"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

CpChallengePage.displayName = 'CpChallengePage'
